import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useInterval } from 'ahooks';
import { useAppSelector } from '@/store';
import useMessageRedux from '@/store/message-redux';
import { PageEnum } from '@/enums/pageEnum';
import {
  getAiCallPending,
  type AiCallPending,
  type AiCallVo,
} from '@/facilities/modules/aiCall';
import { LiveDot } from '@/pages/ai-hotline/LiveBits';

const POLL_MS = 4000;
const PERMISSION = 'ai_hotline';

const beep = () => {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.15);
    osc.stop(ctx.currentTime + 0.35);
    osc.onended = () => ctx.close();
  } catch {
    /* audio not available */
  }
};

/**
 * Header bell: polls the AI hotline for calls waiting for review and raises a notification
 * whenever a new one arrives.
 */
export default function AiCallNotifier() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = useMessageRedux();
  const roles = useAppSelector((state) => state.app.roles);
  const enabled = roles.includes(PERMISSION);
  const [pending, setPending] = useState<AiCallPending | null>(null);
  const lastSeen = useRef<number | null>(null);
  const seenLive = useRef<Set<number> | null>(null);

  const goToCall = useCallback(
    (id?: number | null) =>
      navigate(id ? `${PageEnum.AI_HOTLINE}?id=${id}` : PageEnum.AI_HOTLINE),
    [navigate],
  );

  const poll = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await getAiCallPending();
      const data: AiCallPending = res.data?.body || {
        count: 0,
        latestId: 0,
        latest: null,
      };
      setPending(data);
      const live: AiCallVo[] = data.live || [];
      if (lastSeen.current === null) {
        // never alert for calls that were already there when the page opened
        lastSeen.current = data.latestId;
        seenLive.current = new Set(live.map((c) => c.id));
        return;
      }
      const seen = seenLive.current || new Set<number>();
      live
        .filter((c) => !seen.has(c.id))
        .forEach((c) => {
          seen.add(c.id);
          const key = `ai-call-live-${c.id}`;
          notification.open({
            key,
            message: (
              <span className="flex items-center gap-2">
                <LiveDot /> {t('hotline_incomingCall')}
              </span>
            ),
            description: `${c.callerNumber || c.callSid} · ${c.formTypeFormat || ''}`,
            placement: 'topRight',
            duration: 15,
            actions: (
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  notification.destroy(key);
                  goToCall(c.id);
                }}
              >
                {t('hotline_watchLive')}
              </Button>
            ),
          });
          beep();
        });
      seenLive.current = seen;
      if (data.latestId > lastSeen.current) {
        lastSeen.current = data.latestId;
        const latest = data.latest;
        const who = latest?.formData?.fullName || latest?.callerNumber || '';
        const title =
          latest?.summaryTitle || latest?.formTypeFormat || t('hotline_newCall');
        notification.open({
          key: `ai-call-${data.latestId}`,
          message: t('hotline_newCall'),
          description: `${who ? `${who} · ` : ''}${title}`,
          placement: 'topRight',
          duration: 0,
          actions: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                notification.destroy(`ai-call-${data.latestId}`);
                goToCall(latest?.id);
              }}
            >
              {t('hotline_review')}
            </Button>
          ),
        });
        beep();
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(t('hotline_newCall'), {
            body: `${who} · ${title}`,
            tag: `ai-call-${data.latestId}`,
          });
          n.onclick = () => {
            window.focus();
            goToCall(latest?.id);
          };
        }
      }
    } catch {
      /* ignore polling errors, the next tick will retry */
    }
  }, [enabled, notification, t, goToCall]);

  useEffect(() => {
    poll();
  }, [poll]);

  useInterval(poll, enabled ? POLL_MS : undefined);

  if (!enabled) return null;

  const liveCall = pending?.live?.[0];

  return (
    <Badge
      count={pending?.count || 0}
      size="small"
      offset={[-6, 6]}
      title={t('hotline_pendingReview')}
    >
      <span className="relative inline-block">
        <Button
          type="text"
          className="mr-3"
          data-testid="ai-call-bell"
          icon={<BellOutlined />}
          title={liveCall ? t('hotline_liveNow') : undefined}
          onClick={() => {
            if ('Notification' in window && Notification.permission === 'default') {
              Notification.requestPermission().catch(() => undefined);
            }
            goToCall(liveCall?.id ?? pending?.latest?.id);
          }}
        />
        {liveCall && <LiveDot className="absolute left-1 top-1 pointer-events-none" />}
      </span>
    </Badge>
  );
}
